import { useRef } from "react";
import { motion } from "framer-motion";
import { Play } from "lucide-react";
import { mediaUrl } from "@/lib/api";
import { HlsVideo } from "@/components/media/HlsVideo";
import { ItemTools } from "./ItemTools";

const PlayBadge = ({ big }) => (
  <div className={`relative flex items-center justify-center ${big ? "h-32 w-32 sm:h-40 sm:w-40" : "h-16 w-16"}`}>
    {big && (
      <svg viewBox="0 0 200 200" className="spin-slow absolute inset-0 h-full w-full" aria-hidden>
        <defs><path id="playring" d="M100,100 m-80,0 a80,80 0 1,1 160,0 a80,80 0 1,1 -160,0" /></defs>
        <text className="font-mono-label" fontSize="13" letterSpacing="6" fill="#fff"><textPath href="#playring">PLAY VIDEO ✶ PLAY VIDEO ✶ PLAY VIDEO ✶</textPath></text>
      </svg>
    )}
    <span className={`flex items-center justify-center rounded-full bg-[#E62314] text-white shadow-2xl transition-transform duration-500 group-hover:scale-110 ${big ? "h-16 w-16 sm:h-20 sm:w-20" : "h-12 w-12"}`}>
      <Play className={big ? "h-7 w-7 translate-x-0.5" : "h-5 w-5 translate-x-0.5"} fill="currentColor" />
    </span>
  </div>
);

const VideoCard = ({ v, i, list, editing, reload, onOpen, featured }) => {
  const ref = useRef(null);
  const play = () => { const el = ref.current; if (el) { el.muted = true; el.play().catch(() => {}); } };
  const stop = () => { const el = ref.current; if (el) { el.pause(); el.currentTime = 0.5; } };
  return (
    <motion.div initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.1 }}
      transition={{ duration: 0.8, delay: featured ? 0 : (i % 2) * 0.1, ease: [0.22, 1, 0.36, 1] }}
      onMouseEnter={play} onMouseLeave={stop} onClick={() => onOpen(i)} data-testid={`video-${i}`}
      className={`group relative cursor-pointer overflow-hidden bg-black ${featured ? "aspect-[4/3] sm:aspect-[21/9]" : "aspect-video"}`}>
      <HlsVideo ref={ref} src={`${mediaUrl(v.media_id)}#t=0.5`} muted loop playsInline preload="metadata"
        className="h-full w-full object-cover opacity-80 transition-[opacity,transform] duration-700 group-hover:scale-[1.03] group-hover:opacity-100" />
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/80 via-black/10 to-black/30" />
      <div className="pointer-events-none absolute inset-0 flex items-center justify-center"><PlayBadge big={featured} /></div>
      <div className="pointer-events-none absolute inset-x-0 bottom-0 flex items-end justify-between gap-4 p-5 sm:p-8 text-white">
        <div>
          <p className="font-mono-label text-[11px] uppercase tracking-[0.25em] text-white/75">{featured ? "Featured" : `Video ${String(i + 1).padStart(2, "0")}`}</p>
          {v.title && <p className={`mt-2 font-display font-extrabold tracking-tight ${featured ? "text-3xl sm:text-5xl" : "text-xl"}`}>{v.title}</p>}
        </div>
      </div>
      {editing && <ItemTools item={v} i={i} list={list} kind="video" reload={reload} />}
    </motion.div>
  );
};

export const VideoReel = ({ videos, editing, reload, onOpen }) => {
  const [first, ...rest] = videos;
  return (
    <div className="flex flex-col gap-4" data-testid="video-reel">
      {first && <VideoCard v={first} i={0} list={videos} editing={editing} reload={reload} onOpen={onOpen} featured />}
      {rest.length > 0 && (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          {rest.map((v, j) => <VideoCard key={v.id} v={v} i={j + 1} list={videos} editing={editing} reload={reload} onOpen={onOpen} />)}
        </div>
      )}
    </div>
  );
};
